import {
  closestCenter,
  DragOverlay,
  DndContext,
  Modifier,
  SensorDescriptor,
  SensorOptions,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  HeaderGroup,
  Row as TableRow,
  TableBodyPropGetter,
  TableBodyProps,
  TablePropGetter,
  TableProps,
} from 'react-table';

import { Visible } from 'coreOld/components';
import { Loader, Scrollable } from 'coreOld/elements';

import { Body } from './Body';
import { Footer } from './Footer';
import { Header } from './Header';
import { Row } from './Row';
import { infiniteScrollStyle, NoRecordsFound, TableBody, TableWrapper } from './tableStyle';

interface IProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows?: TableRow<any>[];
  prepareRow: (row: TableRow) => void;
  onRowClick?;
  isLoading?: boolean;
  hasRowSelection?: boolean;
  hasFooter?: boolean;
  hasBigSubheader?: boolean;
  hasFilters?: boolean;
  hasNextPage?: boolean;
  isScrollable?: boolean;
  bodyHeightAuto?: boolean;
  fetchNextPage?;
  isFetchingNextPage?: boolean;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  getTableProps?: (propGetter?: TablePropGetter<any>) => TableProps;
  getTableBodyProps?: (propGetter?: TableBodyPropGetter<any>) => TableBodyProps;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  headerGroups?: HeaderGroup[];
  headerLines?: number;
  footerGroups?: HeaderGroup[];
  footerLines?: number;
  borderBottom?: boolean;
  borderTop?: boolean;
  borderRight?: boolean;
  tableBodyStyle?: CSSProperties;
  alternating?: boolean;
  scrollableTarget?: string;
  fullWidth?: boolean;
  noItemsFoundText?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isRowDeleted?: (item: any) => boolean;
  hasRowReordering?: boolean;
  activeId?: number | string;
  selectedReorderingRow?: TableRow;
  sensors?: SensorDescriptor<SensorOptions>[];
  onDragStart?: (event) => void;
  onDragEnd?: (event) => void;
  onDragCancel?: () => void;
  modifiers?: Modifier[];
}

const TableView = ({
  rows,
  prepareRow,
  onRowClick,
  isLoading,
  hasRowSelection,
  hasFooter,
  hasBigSubheader,
  hasFilters,
  hasNextPage,
  isScrollable,
  bodyHeightAuto,
  fetchNextPage,
  isFetchingNextPage,
  getTableProps,
  getTableBodyProps,
  headerGroups,
  headerLines,
  footerGroups,
  footerLines,
  borderBottom,
  borderTop,
  borderRight,
  tableBodyStyle,
  alternating,
  scrollableTarget,
  fullWidth,
  noItemsFoundText,
  isRowDeleted,

  hasRowReordering,
  activeId,
  selectedReorderingRow,
  sensors,
  onDragStart,
  onDragEnd,
  onDragCancel,
  modifiers,
}: IProps) => {
  const { t } = useTranslation();

  const rowIds = useMemo(() => rows?.map(({ original }) => original.id), [rows]);

  const bodyContent = (
    <Body
      rows={rows}
      prepareRow={prepareRow}
      borderRight={borderRight}
      borderBottom={borderBottom}
      alternating={alternating}
      onRowClick={onRowClick}
      tableBodyStyle={tableBodyStyle}
      fullWidth={fullWidth}
      isRowDeleted={isRowDeleted}
      hasRowReordering={hasRowReordering}
      selectedReorderingRow={selectedReorderingRow}
    />
  );

  const tableContent = (
    <TableWrapper {...getTableProps()}>
      <Header
        headerGroups={headerGroups}
        borderBottom={borderBottom}
        borderTop={borderTop}
        lines={headerLines}
        hasRowSelection={hasRowSelection}
        fullWidth={fullWidth}
      />

      <Scrollable
        id={scrollableTarget}
        hasFooter={hasFooter}
        hasFilters={hasFilters}
        isScrollable={isScrollable}
        bodyHeightAuto={bodyHeightAuto}
        hasBigSubheader={hasBigSubheader}
        isLoading={isLoading}
      >
        <Visible when={rows.length === 0 && !isLoading}>
          <NoRecordsFound>{noItemsFoundText || t('messageNoItemsFound')}</NoRecordsFound>
        </Visible>

        <Visible when={isLoading}>
          <Loader fullHeight overlay />
        </Visible>

        <Visible when={rows.length > 0}>
          <InfiniteScroll
            dataLength={rows.length}
            next={fetchNextPage}
            hasMore={hasNextPage}
            scrollableTarget={scrollableTarget}
            loader={isFetchingNextPage && <Loader />}
            style={isScrollable && infiniteScrollStyle(isLoading)}
          >
            <TableBody {...getTableBodyProps()}>
              {hasRowReordering && (
                <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
                  {bodyContent}
                </SortableContext>
              )}

              {!hasRowReordering && bodyContent}
            </TableBody>
          </InfiniteScroll>
        </Visible>
      </Scrollable>

      <Visible when={footerLines > 0}>
        <Footer footerGroups={footerGroups} lines={footerLines} />
      </Visible>
    </TableWrapper>
  );

  if (!hasRowReordering) {
    return tableContent;
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      collisionDetection={closestCenter}
      modifiers={modifiers}
    >
      {tableContent}

      <DragOverlay>
        {activeId && <Row row={selectedReorderingRow} fullWidth={fullWidth} isStaticRow />}
      </DragOverlay>
    </DndContext>
  );
};

export default TableView;
